import { UseInfiniteQueryResult, useInfiniteQuery } from 'react-query';
import pDebounce from 'p-debounce';

import { useAuth } from 'src/hooks';

import { hospitalFormLogUrl } from 'src/utils/urls';
import { pageLength } from 'src/utils/appConstants';
import { setListRequestData } from '../utils/listReqData';
import { activityStore } from '../store/activityStore';

import { ActivityLogFilterType } from '../types/filterOptionsType';
import { PersonAlertLogType } from '../types/activityLogType';

async function fetchHospitalFormLogs({
  requestData,
  token,
  tokenType,
  pageParam,
}: TVariables): Promise<TPage> {
  requestData.append('start', `${pageParam}`);
  activityStore.getState().setPageParam(pageParam);
  const response = await fetch(hospitalFormLogUrl, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

export type TVariables = {
  requestData: any;
  token: string;
  tokenType: string;
  pageParam: number;
};

export type TError = { message: string };

export type TPage = {
  data: Array<PersonAlertLogType>;
  draw: number;
  error: null;
  isAutoRefreshEnabled: boolean;
  pageNumber: number;
  recordsFiltered: number;
  recordsTotal: number;
};

const debouncedFetch = pDebounce(fetchHospitalFormLogs, 500);

function useGetHospitalForm(
  filterOptions: ActivityLogFilterType,
  selectedToggle: string,
  isActivityEmpty?: boolean,
): UseInfiniteQueryResult<TPage, unknown> {
  const { token, tokenType } = useAuth();
  return useInfiniteQuery(
    ['get_hospital_form_logs', filterOptions, selectedToggle],
    ({ pageParam = 0 }) => {
      const requestData = setListRequestData(filterOptions, selectedToggle);
      return debouncedFetch({ token, tokenType, requestData, pageParam });
    },
    {
      getNextPageParam: (lastPage: TPage, allPages: Array<TPage>) => {
        if (lastPage !== null && lastPage?.data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TPage, allPages: Array<TPage>) => {
        if (firstPage !== null && firstPage?.data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      enabled: selectedToggle === 'hospitalForm' && !isActivityEmpty,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetHospitalForm };
